import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import Alert from '../components/Alert';
import { useForm } from '../hooks/useForm';
import LoadingSpinner from '../components/LoadingSpinner';

const UserProfile = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { user, setUser, logout, refreshUser, isSubscriptionActive } = useAuth();

    // Profile editing state
    const [isEditingProfile, setIsEditingProfile] = useState(false);
    const [isEditingPassword, setIsEditingPassword] = useState(false);
    const { formData: profileData, handleChange: handleProfileChange, setFormData: setProfileData } = useForm({
        firstName: user?.firstName || '',
        lastName: user?.lastName || '',
        licenseNumber: user?.licenseNumber || '',
    });

    // Password change state
    const { formData: passwordData, handleChange: handlePasswordChange, resetForm: resetPasswordForm } = useForm({
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
    });

    // Preferences state
    const [swapLegSides, setSwapLegSides] = useState(user?.preferences?.swapLegSides || false);

    // Delete account state
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [deleteConfirmText, setDeleteConfirmText] = useState('');

    // Subscription upgrade state
    const [showUpgradeModal, setShowUpgradeModal] = useState(false);
    const [upgradePlan, setUpgradePlan] = useState('PRO');
    const [upgradeBillingPeriod, setUpgradeBillingPeriod] = useState('monthly');
    const [upgradeLoading, setUpgradeLoading] = useState(false);

    // UI states
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    // Update profile data when user changes
    React.useEffect(() => {
        if (user) {
            setProfileData({
                firstName: user.firstName || '',
                lastName: user.lastName || '',
                licenseNumber: user.licenseNumber || '',
            });
            setSwapLegSides(user.preferences?.swapLegSides || false);
        }
    }, [user, setProfileData]);

    const handleUpdateProfile = async (e) => {
        e.preventDefault();
        setError('');
        setSuccess('');
        setLoading(true);

        try {
            const response = await axios.put('/api/auth/profile', profileData);
            setUser(response.data);
            setSuccess(t('profile.edit.successMessage'));
            setIsEditingProfile(false);
        } catch (err) {
            setError(err.response?.data?.message || t('profile.edit.errorMessage'));
        } finally {
            setLoading(false);
        }
    };

    const handleCancelEdit = () => {
        setProfileData({
            firstName: user?.firstName || '',
            lastName: user?.lastName || '',
            licenseNumber: user?.licenseNumber || '',
        });
        setIsEditingProfile(false);
        setError('');
    };

    const handleUpdatePassword = async (e) => {
        e.preventDefault();
        setError('');
        setSuccess('');

        if (passwordData.newPassword !== passwordData.confirmPassword) {
            setError(t('profile.password.passwordMismatch'));
            return;
        }

        if (passwordData.newPassword.length < 8) {
            setError(t('profile.password.passwordTooShort'));
            return;
        }

        setLoading(true);

        try {
            await axios.put('/api/auth/password', {
                currentPassword: passwordData.currentPassword,
                newPassword: passwordData.newPassword,
            });
            setSuccess(t('profile.password.successMessage'));
            setIsEditingPassword(false);
            resetPasswordForm();
        } catch (err) {
            setError(err.response?.data?.message || t('profile.password.errorMessage'));
        } finally {
            setLoading(false);
        }
    };

    const handleCancelPasswordEdit = () => {
        resetPasswordForm();
        setIsEditingPassword(false);
        setError('');
    };

    const handleUpdatePreferences = async (newSwapValue) => {
        setError('');
        setSuccess('');

        try {
            const response = await axios.put('/api/auth/preferences', {
                swapLegSides: newSwapValue,
            });
            setUser(response.data);
            setSwapLegSides(newSwapValue);
            setSuccess(t('profile.preferences.successMessage'));
        } catch (err) {
            setError(err.response?.data?.message || t('profile.preferences.errorMessage'));
        }
    };

    const getDaysRemaining = () => {
        if (!user?.subscriptionExpiry) return null;
        const expiry = new Date(user.subscriptionExpiry);
        const now = new Date();
        const daysLeft = Math.ceil((expiry - now) / (1000 * 60 * 60 * 24));
        return daysLeft > 0 ? daysLeft : 0;
    };

    const getAccessibleFeatures = () => {
        const features = {
            'FREE-TRIAL': ['off-the-shelf', 'made-to-measure'],
            'BASIC': ['off-the-shelf'],
            'PRO': ['off-the-shelf', 'made-to-measure'],
        };
        if (user?.subscription === 'FREE-TRIAL' && getDaysRemaining() === 0) return [];
        return features[user?.subscription] || [];
    };

    const handleUpgrade = async () => {
        setUpgradeLoading(true);
        setError('');
        setSuccess('');
        try {
            const response = await axios.put('/api/organizations/upgrade-subscription', {
                plan: upgradePlan,
                billingPeriod: upgradeBillingPeriod,
            });
            setUser(response.data.user);
            setSuccess(t('profile.subscription.upgradeSuccess'));
            setShowUpgradeModal(false);
        } catch (err) {
            setError(err.response?.data?.message || t('profile.subscription.upgradeError'));
        } finally {
            setUpgradeLoading(false);
        }
    };

    const handleDeleteAccount = async () => {
        if (deleteConfirmText !== 'DELETE') {
            setError(t('profile.accountActions.deleteAccount.mustTypeDelete'));
            return;
        }

        setLoading(true);
        setError('');

        try {
            await axios.delete('/api/auth/account');
            logout();
            navigate('/', { state: { message: t('profile.accountActions.deleteAccount.successMessage') } });
        } catch (err) {
            setError(err.response?.data?.message || t('profile.accountActions.deleteAccount.errorMessage'));
            setLoading(false);
        }
    };

    return (
        <div className="page-transition min-h-screen bg-gray-50 py-8">
            <div className="container mx-auto px-4 max-w-4xl">
                <div className="mb-8">
                    <h1 className="text-3xl font-bold mb-2">{t('profile.title')}</h1>
                    <p className="text-gray-600">{t('profile.subtitle')}</p>
                </div>

                <Alert type="error" message={error} onDismiss={() => setError('')} />
                <Alert type="success" message={success} onDismiss={() => setSuccess('')} />

                {/* Profile Information */}
                <div className="card mb-6">
                    <div className="flex justify-between items-center mb-4">
                        <h2 className="text-xl font-semibold">{t('profile.information.title')}</h2>
                        {!isEditingProfile && !isEditingPassword && (
                            <button
                                onClick={() => setIsEditingProfile(true)}
                                className="btn-primary"
                            >
                                {t('profile.edit.button')}
                            </button>
                        )}
                    </div>

                    {isEditingProfile ? (
                        <form onSubmit={handleUpdateProfile}>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        {t('profile.information.firstName')} *
                                    </label>
                                    <input
                                        type="text"
                                        name="firstName"
                                        value={profileData.firstName}
                                        onChange={handleProfileChange}
                                        required
                                        className="input-field"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        {t('profile.information.lastName')} *
                                    </label>
                                    <input
                                        type="text"
                                        name="lastName"
                                        value={profileData.lastName}
                                        onChange={handleProfileChange}
                                        required
                                        className="input-field"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        {t('profile.information.licenseNumber')}
                                    </label>
                                    <input
                                        type="text"
                                        name="licenseNumber"
                                        value={profileData.licenseNumber}
                                        onChange={handleProfileChange}
                                        placeholder={t('profile.information.licenseNumberPlaceholder')}
                                        className="input-field"
                                    />
                                </div>
                            </div>
                            <div className="flex gap-3">
                                <button
                                    type="submit"
                                    disabled={loading}
                                    className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    {loading ? <LoadingSpinner size="sm" /> : t('common.save')}
                                </button>
                                <button
                                    type="button"
                                    onClick={handleCancelEdit}
                                    disabled={loading}
                                    className="btn-secondary"
                                >
                                    {t('common.cancel')}
                                </button>
                            </div>
                        </form>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    {t('profile.information.firstName')}
                                </label>
                                <input
                                    type="text"
                                    value={user?.firstName || ''}
                                    disabled
                                    className="input-field bg-gray-50"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    {t('profile.information.lastName')}
                                </label>
                                <input
                                    type="text"
                                    value={user?.lastName || ''}
                                    disabled
                                    className="input-field bg-gray-50"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    {t('profile.information.email')}
                                </label>
                                <input
                                    type="email"
                                    value={user?.email || ''}
                                    disabled
                                    className="input-field bg-gray-50"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    {t('profile.information.role')}
                                </label>
                                <input
                                    type="text"
                                    value={user?.role || ''}
                                    disabled
                                    className="input-field bg-gray-50 capitalize"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    {t('profile.information.licenseNumber')}
                                </label>
                                <input
                                    type="text"
                                    value={user?.licenseNumber || t('profile.information.notProvided')}
                                    disabled
                                    className="input-field bg-gray-50"
                                />
                            </div>
                            {user?.authProvider && (
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        {t('profile.information.authMethod')}
                                    </label>
                                    <div className="flex items-center gap-2">
                                        <input
                                            type="text"
                                            value={user.authProvider === 'google' ? t('profile.information.google') : t('profile.information.emailPassword')}
                                            disabled
                                            className="input-field bg-gray-50 capitalize"
                                        />
                                        {user.authProvider === 'google' && user.profilePicture && (
                                            <img
                                                src={user.profilePicture}
                                                alt="Profile"
                                                className="w-10 h-10 rounded-full"
                                            />
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {user?.organization && (
                        <div className="mt-6 p-4 bg-blue-50 rounded-lg">
                            <h3 className="font-semibold text-blue-900 mb-2">{t('profile.organization.title')}</h3>
                            <p className="text-blue-800">
                                <span className="font-medium">{t('profile.organization.name')}:</span> {user.organization.name}
                            </p>
                            <p className="text-blue-800">
                                <span className="font-medium">{t('profile.subscription.tier')}:</span>{' '}
                                {user.subscription}
                            </p>
                        </div>
                    )}
                </div>

                {/* Password Change Section - Only for local auth users */}
                {user?.authProvider === 'local' && (
                    <div className="card mb-6">
                        <div className="flex justify-between items-center mb-4">
                            <h2 className="text-xl font-semibold">{t('profile.password.title')}</h2>
                            {!isEditingPassword && !isEditingProfile && (
                                <button
                                    onClick={() => setIsEditingPassword(true)}
                                    className="btn-secondary"
                                >
                                    {t('profile.password.changeButton')}
                                </button>
                            )}
                        </div>

                        {isEditingPassword ? (
                            <form onSubmit={handleUpdatePassword} className="space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        {t('profile.password.currentPassword')} *
                                    </label>
                                    <input
                                        type="password"
                                        name="currentPassword"
                                        value={passwordData.currentPassword}
                                        onChange={handlePasswordChange}
                                        required
                                        className="input-field"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        {t('profile.password.newPassword')} *
                                    </label>
                                    <input
                                        type="password"
                                        name="newPassword"
                                        value={passwordData.newPassword}
                                        onChange={handlePasswordChange}
                                        required
                                        minLength={8}
                                        className="input-field"
                                    />
                                    <p className="text-sm text-gray-500 mt-1">
                                        {t('profile.password.requirements')}
                                    </p>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        {t('profile.password.confirmPassword')} *
                                    </label>
                                    <input
                                        type="password"
                                        name="confirmPassword"
                                        value={passwordData.confirmPassword}
                                        onChange={handlePasswordChange}
                                        required
                                        className="input-field"
                                    />
                                </div>
                                <div className="flex gap-3">
                                    <button
                                        type="submit"
                                        disabled={loading}
                                        className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        {loading ? <LoadingSpinner size="sm" /> : t('profile.password.updateButton')}
                                    </button>
                                    <button
                                        type="button"
                                        onClick={handleCancelPasswordEdit}
                                        disabled={loading}
                                        className="btn-secondary"
                                    >
                                        {t('common.cancel')}
                                    </button>
                                </div>
                            </form>
                        ) : (
                            <p className="text-gray-600">
                                {t('profile.password.description')}
                            </p>
                        )}
                    </div>
                )}

                {/* Preferences Section */}
                <div className="card mb-6">
                    <h2 className="text-xl font-semibold mb-4">{t('profile.preferences.title')}</h2>
                    <div className="space-y-4">
                        <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                            <div>
                                <h3 className="font-medium text-gray-900">
                                    {t('profile.preferences.swapLegSides')}
                                </h3>
                                <p className="text-sm text-gray-600 mt-1">
                                    {t('profile.preferences.swapLegSidesDescription')}
                                </p>
                            </div>
                            <label className="relative inline-flex items-center cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={swapLegSides}
                                    onChange={(e) => handleUpdatePreferences(e.target.checked)}
                                    className="sr-only peer"
                                />
                                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                            </label>
                        </div>
                    </div>
                </div>

                {/* Subscription Status Section */}
                <div className={`card mb-6 ${!isSubscriptionActive() ? 'border-2 border-red-200' : ''}`}>
                    <div className="flex justify-between items-center mb-4">
                        <h2 className="text-xl font-semibold">{t('profile.subscription.status')}</h2>
                        <span className={`px-3 py-1 rounded-full text-sm font-semibold ${user?.subscription === 'PRO' && isSubscriptionActive()
                            ? 'bg-green-100 text-green-700'
                            : user?.subscription === 'BASIC' && isSubscriptionActive()
                                ? 'bg-blue-100 text-blue-700'
                                : user?.subscription === 'FREE-TRIAL' && isSubscriptionActive()
                                    ? 'bg-amber-100 text-amber-700'
                                    : 'bg-red-100 text-red-700'
                            }`}>
                            {user?.subscription === 'PRO'
                                ? t('profile.subscription.pro')
                                : user?.subscription === 'BASIC'
                                    ? t('profile.subscription.basic')
                                    : t('profile.subscription.freeTrial')}
                        </span>
                    </div>

                    <div className="space-y-4">
                        {/* Expiry info */}
                        {user?.subscriptionExpiry && (
                            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                                <span className="text-sm text-gray-600">{t('profile.subscription.expiresOn')}</span>
                                <span className="text-sm font-medium">
                                    {new Date(user.subscriptionExpiry).toLocaleDateString()}
                                    {getDaysRemaining() !== null && (
                                        <span className={`ml-2 ${getDaysRemaining() <= 7 ? 'text-red-600' : 'text-gray-500'}`}>
                                            ({t('profile.subscription.daysRemaining', { count: getDaysRemaining() })})
                                        </span>
                                    )}
                                </span>
                            </div>
                        )}

                        {/* Features list */}
                        <div>
                            <p className="text-sm font-medium text-gray-700 mb-2">{t('profile.subscription.features')}</p>
                            <ul className="space-y-2">
                                <li className="flex items-center gap-2">
                                    <svg className={`w-4 h-4 flex-shrink-0 ${getAccessibleFeatures().includes('off-the-shelf') ? 'text-green-500' : 'text-gray-300'}`} fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                    </svg>
                                    <span className={`text-sm ${getAccessibleFeatures().includes('off-the-shelf') ? 'text-gray-800' : 'text-gray-400'}`}>
                                        {t('profile.subscription.featureOffTheShelf')}
                                    </span>
                                </li>
                                <li className="flex items-center gap-2">
                                    <svg className={`w-4 h-4 flex-shrink-0 ${getAccessibleFeatures().includes('made-to-measure') ? 'text-green-500' : 'text-gray-300'}`} fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                    </svg>
                                    <span className={`text-sm ${getAccessibleFeatures().includes('made-to-measure') ? 'text-gray-800' : 'text-gray-400'}`}>
                                        {t('profile.subscription.featureMadeToMeasure')}
                                    </span>
                                </li>
                            </ul>
                        </div>

                        {/* Upgrade/Renew CTA — for FREE-TRIAL, BASIC, and expired PRO */}
                        {(user?.subscription !== 'PRO' || !isSubscriptionActive()) && (
                            !showUpgradeModal ? (
                                <div className="pt-2">
                                    {/* Active FREE-TRIAL promo */}
                                    {user?.subscription === 'FREE-TRIAL' && isSubscriptionActive() && (
                                        <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-3 mb-3">
                                            {t('profile.subscription.upgradePromo')}
                                        </p>
                                    )}
                                    {/* Expired warning for any plan */}
                                    {!isSubscriptionActive() && (
                                        <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-3 flex items-center gap-2">
                                            <svg className="w-4 h-4 text-red-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                            </svg>
                                            <p className="text-sm text-red-700 font-medium">
                                                {user?.subscription === 'FREE-TRIAL'
                                                    ? t('profile.subscription.trialExpired')
                                                    : t('profile.subscription.planExpired', { plan: user?.subscription })}
                                            </p>
                                        </div>
                                    )}
                                    <Link
                                        to="/organization-settings"
                                        state={{ tab: 'subscription' }}
                                        className="btn-primary inline-block"
                                    >
                                        {!isSubscriptionActive() && user?.subscription !== 'FREE-TRIAL'
                                            ? t('profile.subscription.payNow')
                                            : t('profile.subscription.upgradeNow')}
                                    </Link>
                                </div>
                            ) : (
                                <div className="mt-2 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                                    <h3 className="font-semibold text-blue-900 mb-4">{t('profile.subscription.choosePlan')}</h3>

                                    {/* Plan selector */}
                                    <div className="grid grid-cols-2 gap-3 mb-4">
                                        {['BASIC', 'PRO'].map((plan) => (
                                            <button
                                                key={plan}
                                                type="button"
                                                onClick={() => setUpgradePlan(plan)}
                                                className={`p-3 border-2 rounded-lg text-left transition-colors ${upgradePlan === plan
                                                    ? 'border-blue-600 bg-white'
                                                    : 'border-gray-200 bg-white hover:border-blue-300'
                                                    }`}
                                            >
                                                <div className="font-semibold text-gray-900">
                                                    {plan === 'BASIC' ? t('profile.subscription.basic') : t('profile.subscription.pro')}
                                                </div>
                                                <div className="text-sm text-gray-600 mt-1">
                                                    {upgradeBillingPeriod === 'monthly'
                                                        ? (plan === 'BASIC' ? '€49/mo' : '€79/mo')
                                                        : (plan === 'BASIC' ? '€490/yr' : '€790/yr')}
                                                </div>
                                                {plan === 'PRO' && (
                                                    <div className="text-xs text-purple-600 mt-1 font-medium">
                                                        + {t('profile.subscription.featureMadeToMeasure')}
                                                    </div>
                                                )}
                                            </button>
                                        ))}
                                    </div>

                                    {/* Billing period toggle */}
                                    <div className="flex items-center gap-3 mb-4">
                                        <span className={`text-sm ${upgradeBillingPeriod === 'monthly' ? 'font-semibold text-gray-900' : 'text-gray-500'}`}>
                                            {t('profile.subscription.monthly')}
                                        </span>
                                        <button
                                            type="button"
                                            onClick={() => setUpgradeBillingPeriod(prev => prev === 'monthly' ? 'yearly' : 'monthly')}
                                            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 ${upgradeBillingPeriod === 'yearly' ? 'bg-blue-600' : 'bg-gray-200'}`}
                                        >
                                            <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${upgradeBillingPeriod === 'yearly' ? 'translate-x-6' : 'translate-x-1'}`} />
                                        </button>
                                        <span className={`text-sm ${upgradeBillingPeriod === 'yearly' ? 'font-semibold text-gray-900' : 'text-gray-500'}`}>
                                            {t('profile.subscription.yearly')}
                                        </span>
                                    </div>

                                    {/* Action buttons */}
                                    <div className="flex gap-3">
                                        <button
                                            onClick={handleUpgrade}
                                            disabled={upgradeLoading}
                                            className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
                                        >
                                            {upgradeLoading ? <LoadingSpinner size="sm" /> : t('profile.subscription.confirmUpgrade')}
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => setShowUpgradeModal(false)}
                                            disabled={upgradeLoading}
                                            className="btn-secondary"
                                        >
                                            {t('common.cancel')}
                                        </button>
                                    </div>
                                </div>
                            )
                        )}
                    </div>
                </div>

                {/* Danger Zone */}
                <div className="card border-2 border-red-200">
                    <h2 className="text-xl font-semibold mb-4 text-red-600">{t('profile.accountActions.title')}</h2>
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                        <h3 className="font-semibold text-red-900 mb-2">{t('profile.accountActions.deleteAccount.button')}</h3>
                        <p className="text-sm text-red-800 mb-4">
                            {t('profile.accountActions.deleteAccount.confirmMessage')}
                        </p>
                        <ul className="list-disc list-inside text-sm text-red-800 mb-4 space-y-1">
                            <li>{t('profile.accountActions.deleteAccount.willDelete.profile')}</li>
                            <li>{t('profile.accountActions.deleteAccount.willDelete.measurements')}</li>
                            <li>{t('profile.accountActions.deleteAccount.willDelete.organization')}</li>
                            <li>{t('profile.accountActions.deleteAccount.willDelete.data')}</li>
                        </ul>

                        {!showDeleteConfirm ? (
                            <button
                                onClick={() => setShowDeleteConfirm(true)}
                                className="bg-red-600 text-white px-6 py-3 rounded-lg hover:bg-red-700 active:bg-red-800 transition-colors font-semibold min-h-touch min-w-touch"
                            >
                                {t('profile.accountActions.deleteAccount.button')}
                            </button>
                        ) : (
                            <div className="space-y-4">
                                <div className="bg-white p-4 rounded border border-red-300">
                                    <p className="text-sm font-semibold text-red-900 mb-2">
                                        {t('profile.accountActions.deleteAccount.confirmTitle')}
                                    </p>
                                    <p className="text-sm text-red-800 mb-4">
                                        {t('profile.accountActions.deleteAccount.typeDelete')}
                                    </p>
                                    <input
                                        type="text"
                                        value={deleteConfirmText}
                                        onChange={(e) => setDeleteConfirmText(e.target.value)}
                                        placeholder={t('profile.accountActions.deleteAccount.typeDelete')}
                                        className="input-field mb-2"
                                        autoFocus
                                    />
                                    <div className="flex gap-3">
                                        <button
                                            onClick={handleDeleteAccount}
                                            disabled={loading || deleteConfirmText !== 'DELETE'}
                                            className="bg-red-600 text-white px-6 py-3 rounded-lg hover:bg-red-700 active:bg-red-800 transition-colors font-semibold disabled:opacity-50 disabled:cursor-not-allowed min-h-touch min-w-touch"
                                        >
                                            {loading ? t('common.loading') : t('profile.accountActions.deleteAccount.confirmButton')}
                                        </button>
                                        <button
                                            onClick={() => {
                                                setShowDeleteConfirm(false);
                                                setDeleteConfirmText('');
                                                setError('');
                                            }}
                                            disabled={loading}
                                            className="bg-gray-300 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-400 transition-colors font-semibold disabled:opacity-50"
                                        >
                                            {t('profile.accountActions.deleteAccount.cancelButton')}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default UserProfile;
